import { cloneElement, isValidElement, useId } from 'react'
import type { ReactElement, ReactNode } from 'react'
import type { Calibration } from '../api'
import { ApiError, fmtTime } from '../api'
import { useT } from '../lang'

/** A place name as a person writes it, not as the register stores it.
 *
 *  Akvakulturregisteret keys everything in capitals -- `ØYGARDEN`, `BØMLO`,
 *  `HERØY` -- and printing that verbatim under a farm's name set the one word
 *  of context on the page in shouting caps. It reads as machine output because
 *  it is machine output. So the case is normalised for display and the stored
 *  value is left exactly as it arrived.
 *
 *  Only touched when the string is entirely upper case: a name somebody typed
 *  as `Nord-Fosen` already knows its own shape, and `Herøy` must not become
 *  `Herøy` by way of a lossy round trip. Hyphens and spaces both start a word.
 */
export function placeName(s: string): string
export function placeName(s: null | undefined): null
export function placeName(s: string | null | undefined): string | null
export function placeName(s: string | null | undefined): string | null {
  if (!s) return null
  if (s !== s.toLocaleUpperCase('nb')) return s
  return s.toLocaleLowerCase('nb').replace(
    /(^|[\s\-/])(\p{L})/gu, (_, pre: string, ch: string) => pre + ch.toLocaleUpperCase('nb'))
}

/** R5.3, rendered.
 *
 *  The verdict is the server's (app/calibration.py); this only prints it and
 *  names the missing terms so the fix is obvious rather than mysterious.
 *  Uncalibrated is the ordinary state today, so it is styled as information,
 *  not as a fault — a fleet of red warnings teaches people to ignore red.
 */
export function CalibrationBadge({ cal, verbose }: { cal: Calibration; verbose?: boolean }) {
  // An estimated level is absolute — referenced to 1 µPa, not to the site — so
  // it does not belong in the site-relative branch. But it rests on the
  // manufacturer's figure for the model rather than a measurement of this
  // hydrophone, and "calibrated" is a word people quote. Say the weaker thing.
  if (cal.estimated) {
    return (
      <span className="badge on"
        title={`Levels are ${cal.basis}, from the manufacturer's nominal sensitivity for this model rather than a measurement of this unit. Sound enough to compare and to act on; not a laboratory calibration.`}>
        estimated
      </span>
    )
  }
  if (cal.absolute) {
    return (
      <span className="badge on" title={`Levels are ${cal.basis}.`}>
        calibrated
      </span>
    )
  }
  const missing = cal.missing.join(', ')
  return (
    <span className="badge" title={`Missing: ${missing}. Until all three are known, levels are ${cal.basis} and cannot be compared between sites.`}>
      site-relative{verbose && missing ? ` · needs ${missing}` : ''}
    </span>
  )
}

/** How recently a unit must have spoken to count as listening right now.
 *
 *  A node at the events tier ships an hourly roll-up, so it is legitimately
 *  quiet for long stretches. Twenty minutes is short enough that a stopped node
 *  goes still within one screen-watch, and long enough that a healthy one does
 *  not flicker between states while you look at it. */
const ALIVE_WINDOW_MS = 20 * 60 * 1000

export type Contact = 'receiving' | 'offline' | 'never'

/** What can honestly be said about a unit's liveness, in three states.
 *
 *  It used to be a boolean, and the missing state was the one that mattered.
 *  "Not receiving" was rendered as the *absence* of a pulse, which reads as
 *  nothing to report -- so a node unplugged half an hour ago looked exactly
 *  like a quiet one, and the card said nothing about the thing the operator
 *  had come to the page to find out. Observed doing precisely that: a unit
 *  whose power had been pulled kept an unremarkable card for twenty minutes.
 *
 *  `never` is separate from `offline` because the two send someone to look in
 *  different places. A unit that has never spoken was probably never finished
 *  being commissioned; telling that person it "went offline" sends them
 *  hunting a power cut that never happened.
 */
export function contact(lastSeenAt: string | null | undefined): Contact {
  if (!lastSeenAt) return 'never'
  return Date.now() - new Date(lastSeenAt).getTime() < ALIVE_WINDOW_MS
    ? 'receiving'
    : 'offline'
}

/** "I am alive and listening."
 *
 *  The ring travels *inward*, not outward, and that is the whole idea. These
 *  are passive hydrophones -- they never emit anything, they only receive
 *  (ARCHITECTURE.md, "lytt is Norwegian for listen"). An expanding sonar ping
 *  would be a picture of the wrong machine. So a wide, faint ring contracts
 *  and brightens into the dot, and the dot answers with a small beat as it
 *  lands: something arrived.
 *
 *  It is driven by data actually arriving, not by mission state. A mission can
 *  be `active` for a node that has been unplugged for a week, and a pulse on
 *  that would be a liveness claim the system cannot support.
 */
export function Alive({ label }: { label?: string }) {
  return (
    <span className="alive" role="img" aria-label={label ?? 'Receiving data now'}>
      <span className="alive-ring" />
      <span className="alive-ring" />
      <span className="alive-core" />
    </span>
  )
}

/** The still counterpart to <Alive>, and the reason this file has one.
 *
 *  Same size and place in the row as the pulse, so a column of cards reads as
 *  one line of dots and the eye finds the ones that are not beating. Hollow
 *  and static against filled and moving.
 *
 *  Not red, and not styled as an alarm. The brand reserves red for the moment
 *  of detection, and a unit being off is an ordinary fact -- often a
 *  deliberate one. It needs to be unmissable, which is a different property
 *  from being alarming.
 *
 *  The word on screen is "offline" because that is what an operator is asking.
 *  The tooltip is where the honesty lives: there is no broker and no node
 *  state table, so silence is all the receiver has, and a powered-off unit and
 *  a unit with a dead uplink produce exactly the same silence.
 */
export function ContactBadge({
  state, at,
}: { state: Contact; at: string | null | undefined }) {
  if (state === 'receiving') return null
  const title = state === 'never'
    ? 'This unit has never contacted the receiver. It may not have finished commissioning.'
    : `No contact since ${fmtTime(at)}. It may be powered off, or its uplink may be down — nothing here can tell those apart.`
  return (
    <span className="badge quiet" title={title}>
      <span className="dot-quiet" aria-hidden="true" />
      {state === 'never' ? 'never seen' : 'offline'}
    </span>
  )
}

export function StateBadge({ state }: { state: string | null }) {
  if (!state) return <span className="faint">—</span>
  return <span className={`badge${state === 'active' ? ' on' : ' off'}`}>{state}</span>
}

export function ErrorBox({ error }: { error: ApiError | Error | null }) {
  if (!error) return null
  const msg =
    error instanceof ApiError ? error.detail ?? error.code : error.message || 'Request failed'
  return <div className="notice bad">{msg}</div>
}

export function Empty({
  title, children, action,
}: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

export function Rows({ n = 5 }: { n?: number }) {
  return (
    <div className="stack" style={{ padding: 20, gap: 12 }}>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="skeleton" style={{ width: `${94 - i * 7}%` }} />
      ))}
    </div>
  )
}

export function Stat({
  label, value, small, title,
}: { label: string; value: ReactNode; small?: boolean; title?: string }) {
  return (
    <div className="stat" title={title}>
      <div className="label">{label}</div>
      <div className={`v num${small ? ' sm' : ''}`}>{value}</div>
    </div>
  )
}

/** A labelled control.
 *
 *  Two things it used to get wrong, both invisible until you looked for them.
 *  The label was never run through `t()`, so every form in the product showed
 *  English field names inside an otherwise Norwegian page — the translations
 *  existed, nothing reached them. And the `<label>` neither wrapped its control
 *  nor carried `htmlFor`, so it was associated with nothing at all: a screen
 *  reader announced an unlabelled input, and clicking the word did not focus it.
 *
 *  `useId` rather than a caller-supplied id, so no call site has to remember. */
export function Field({
  label, hint, children,
}: { label: string; hint?: ReactNode; children: ReactNode }) {
  const t = useT()
  const id = useId()
  const hintId = `${id}-hint`
  return (
    <div className="field">
      <label htmlFor={id}>{t(label)}</label>
      {isValidElement(children)
        ? cloneElement(children as ReactElement<Record<string, unknown>>,
            { id, ...(hint ? { 'aria-describedby': hintId } : {}) })
        : children}
      {hint && <span className="hint" id={hintId}>{hint}</span>}
    </div>
  )
}
